import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles } from '@/mocks/clsqData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <SEOHead title="文章未找到 - clsq" description="抱歉，您查找的文章不存在。" />
        <ScrollToTop />
        <div className="min-h-screen flex flex-col bg-background-50">
          <Navbar />
          <main className="flex-1 pt-16 flex items-center justify-center">
            <div className="text-center px-4">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-200/60">
                <i className="ri-file-search-line text-3xl text-foreground-400"></i>
              </div>
              <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-3">文章未找到</h1>
              <p className="text-foreground-500 mb-6">抱歉，您查找的文章不存在或已被移除。</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: 'clsq',
    },
    publisher: {
      '@type': 'Organization',
      name: 'clsq',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://clsq.com' },
      { '@type': 'ListItem', position: 2, name: article.category, item: 'https://clsq.com/categories' },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://clsq.com/article/${slug}` },
    ],
  };

  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && a.category === article.category)
    .slice(0, 2);

  return (
    <>
      <SEOHead
        title={`${article.title} - clsq官方`}
        description={article.summary}
        keywords={`clsq,${article.category},企业服务,品牌资讯`}
        canonicalUrl={`https://clsq.com/article/${slug}`}
        ogType="article"
        schemaData={{ ...articleSchema, ...breadcrumbSchema }}
      />
      <ScrollToTop />
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <main className="flex-1 pt-16">
          {/* Breadcrumb */}
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto pt-8">
            <nav className="flex items-center flex-wrap gap-2 text-sm text-foreground-500">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to="/categories" className="hover:text-primary-600 transition-colors cursor-pointer">{article.category}</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 truncate max-w-[200px]">{article.title}</span>
            </nav>
          </div>

          {/* Article Content */}
          <article className="w-full px-4 md:px-6 max-w-4xl mx-auto pt-10 pb-20">
            {/* Meta */}
            <div className="mb-8">
              <span className="inline-block px-3 py-1 bg-accent-100/80 text-accent-700 rounded-full text-xs font-medium mb-4">
                {article.category}
              </span>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  阅读约{article.readTime}
                </span>
              </div>
            </div>

            {/* Article Body */}
            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <div className="mt-16 pt-10 border-t border-background-200/60">
                <h3 className="text-lg font-semibold text-foreground-900 mb-6">相关文章</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="p-5 bg-background-100 rounded-lg border border-background-200/60 hover:border-primary-200/60 transition-all duration-300 cursor-pointer group"
                    >
                      <span className="text-xs text-accent-600 font-medium">{ra.category}</span>
                      <h4 className="mt-2 text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h4>
                      <p className="mt-1 text-xs text-foreground-500 line-clamp-2">{ra.summary}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Back Link */}
            <div className="mt-10">
              <Link
                to="/categories"
                className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回产品分类
              </Link>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}